/**
 * Live funnel preview (owner request, 14 October 2026). In the dashboard the chat runs the
 * clinic's unpublished draft against this in-memory client: nothing is stored, no lead is
 * created, no event is sent, no file is uploaded. "Continue on WhatsApp" returns the message
 * the patient would send, so the dashboard can show it instead of opening WhatsApp.
 */
import type {
  BookingOptionsResponse,
  CreateSessionResponse,
  CreateUploadResponse,
  HandoffLinkResponse,
  HandoffRequest,
  HandoffResponse,
  MessageTemplate,
  ResumeResponse,
  UpsertAnswerRequest,
  UpsertAnswerResponse,
  WidgetConfigResponse,
} from "@wapadesk/schema";
import { DEFAULT_TEMPLATE, renderWhatsAppMessage } from "@wapadesk/message";
import { ApiClient, ApiClientError } from "./api";

export const isPreview = () =>
  typeof location !== "undefined" && location.pathname.replace(/\/+$/, "") === "/preview";

export class PreviewApi extends ApiClient {
  private readonly answers: Record<string, unknown> = {};
  constructor(
    private readonly cfg: WidgetConfigResponse,
    private readonly template: Partial<MessageTemplate> | undefined,
  ) {
    super("");
  }
  override config(): Promise<WidgetConfigResponse> {
    return Promise.resolve(this.cfg);
  }
  override configBySlug(): Promise<WidgetConfigResponse> {
    return Promise.resolve(this.cfg);
  }
  override createSession(): Promise<CreateSessionResponse> {
    return Promise.resolve({
      sessionToken: "preview-session-token-0000",
      leadId: "preview",
      ref: "PREVIEW",
      expiresAt: new Date(Date.now() + 3_600_000).toISOString(),
    });
  }
  override resume(): Promise<ResumeResponse> {
    return Promise.reject(new ApiClientError(404, "preview"));
  }
  override upsertAnswer(body: UpsertAnswerRequest): Promise<UpsertAnswerResponse> {
    this.answers[body.nodeId] = body.value;
    return Promise.resolve({ ok: true, value: body.value, invalidated: [], completed: false });
  }
  override createUpload(): Promise<CreateUploadResponse> {
    return Promise.reject(new ApiClientError(400, "preview_no_uploads"));
  }
  /** The message the patient would send with the answers given so far. */
  message(): string {
    const form = this.cfg.form?.schema;
    if (!form) return "";
    return renderWhatsAppMessage({
      form,
      answers: this.answers as never,
      template: { ...DEFAULT_TEMPLATE, ...(this.template ?? {}) },
      clinic: { name: this.cfg.clinic.name },
      lead: { ref: "PREVIEW", fileCount: 0 },
      recordUrl: "https://app.wapadesk.com/l/PREVIEW",
      pageUrl: "https://your-website.example/page",
      optionLabels: Object.fromEntries(this.cfg.procedures.map((p) => [p.id, p.name])),
    });
  }
  override handoff(body: HandoffRequest): Promise<HandoffResponse> {
    if (body.type === "booking")
      return Promise.resolve({
        type: "booking",
        url: "",
        label: this.cfg.booking.externalLabel ?? "",
        ref: "PREVIEW",
      });
    return Promise.resolve({ type: body.type, url: "", message: this.message(), ref: "PREVIEW" });
  }
  override handoffLink(): Promise<HandoffLinkResponse> {
    return Promise.resolve({
      token: "preview-not-a-real-link",
      expiresAt: new Date(Date.now() + 900_000).toISOString(),
    });
  }
  override bookingOptions(): Promise<BookingOptionsResponse> {
    return Promise.reject(new ApiClientError(409, "preview_no_booking"));
  }
  override events(): Promise<void> {
    return Promise.resolve();
  }
}
